import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject
} from 'react';

function readGapPx(anchor: HTMLElement): number {
  const raw = getComputedStyle(anchor).getPropertyValue('--space-2').trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 8;
}

export interface FixedAnchoredPanelApi {
  panelRef: (node: HTMLDivElement | null) => void;
  style: { top: number; left: number; position: 'fixed' } | undefined;
}

/**
 * Positions a portaled panel below an anchor using viewport coordinates.
 * Re-positions on scroll/resize; closes on Escape and outside click.
 */
export function useFixedAnchoredPanel(
  anchorRef: RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void,
  align: 'start' | 'end' = 'start'
): FixedAnchoredPanelApi {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const setPanelRef = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
  }, []);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const gap = readGapPx(anchor);
    let left = rect.left;
    if (align === 'end' && panel) {
      left = rect.right - panel.offsetWidth;
    }
    // Default below the anchor; flip above when opening below would overflow the
    // viewport bottom and there's room above (e.g. a picker at the bottom of a
    // scrollable inspector). panelRef is attached before this layout effect runs,
    // so the panel is measurable on first positioning.
    //
    // The panel's own offsetHeight can understate its real footprint: pickers
    // render their option list as a position:absolute child (`.pn-pickorcreate-list`,
    // top:100%), which is excluded from offsetHeight. Include that list's extent so
    // the flip reflects what's actually drawn.
    let top = rect.bottom + gap;
    if (panel) {
      const list = panel.querySelector(
        '[role="listbox"]'
      ) as HTMLElement | null;
      const contentH = list
        ? Math.max(panel.offsetHeight, list.offsetTop + list.offsetHeight)
        : panel.offsetHeight;
      if (top + contentH > window.innerHeight && rect.top - gap - contentH >= 0) {
        top = rect.top - gap - contentH;
      }
    }
    setCoords({ top, left });
  }, [align, anchorRef]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    function onPointer(event: MouseEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (anchorRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      onClose();
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointer);
    };
  }, [open, onClose, anchorRef]);

  return {
    panelRef: setPanelRef,
    style: coords
      ? { top: coords.top, left: coords.left, position: 'fixed' }
      : undefined
  };
}
